/*
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * ENGINEERING NOTES
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * !! IMPORTANT !!
 *     1) Do NOT import this class into the following to avoid circular dependency issues:
 *        import { EzEnumerations } from '/ezlibrary/enums/EzEnumerations.js';
 *     2) Do not import from EzExceptions.js or EzHelpers.js (import needed classes directly)
 *     2) Recommended to add any new enumerations to the /ezlibrary/enums/EzEnumerations.js
 *        module to make importing enumerations easier. However, DO NOT add the
 *      enumeration to the EzEnumerations.js class if it imports any dependencies
 *      that might cause a circular reference with other enumerations or classes
 *        import EzEnumerations.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * New Enumeration Template
 *     1) Example enumeration class template is commented out at the bottom of this
 *        file and available at {projectroot}/source-templates/javascript/EzEnumTemplate.js.
 *     2) Copy + paste the template lines at the end of this file into your Javascript enumeration class file
 *     3) Remove the beginning // comments (hint: use block editing mode)
 *     4) Search + replace EzEnumTemplate with the name of the enumeration class you want to create.
 *     5) Edit the private static #ezBuildEnumeration() class to add your enumeration values.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */

import { EzBadParamException } from '/ezlibrary/exceptions/EzBadParamException.js';
import { EzBadStateException } from '/ezlibrary/exceptions/EzBadStateException.js';

import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';
import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
import { EzJson } from '/ezlibrary/helpers/EzJson.js';

import { EzBasicEnumerationData } from '/ezlibrary/enums/EzBasicEnumerationData.js';

/**
 * Defines an enumeration property for an EzEnumeration2 instance.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzEnumerationProperty } from '/ezlibrary/enums/EzEnumeration2.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * @class
 * @typedef EzEnumerationProperty
 */
export class EzEnumerationProperty {
    /**
     * Stores the name of the enumeration property
     * @private
     * @type {string}
     */
    #ezName = null;
    /**
     * @public
     * @readonly
     * @property ezName {string}
     * @returns {string}
     * Returns the name of the enumeration property
     */
    get ezName() {
        return this.#ezName;
    }

    /**
     * @private
     * Stores the value of the enumeration property
     * @type {*}
     */
    #ezValue = null;
    /**
     * @public @readonly @property
     * Returns the value of the enumeration property.
     * @returns {*}
     */
    get ezValue() {
        return this.#ezValue;
    }

    /**
     * @private
     * Stores the optional data associated with the enumeration property.
     * Default is:
     * {
     *     enumName: this.ezName,
     *     enumValue: this.ezValue
     * }
     * @type {string}
     */
    #ezData = null;
    /**
     * @public @property @getter
     * Gets the optional data associated with the enumeration property.
     * @returns {*|object}
     */
    get ezData() {
        return this.#ezData;
    }

    /**
     * @public @constructor
     * Creates a new instance of EzEnumerationProperty
     * @param {string} name
     * @param {*} value
     * @param {undefined|null|*|object} optionalData
     * Default is:
     * {
     *     enumName: this.ezName,
     *     enumValue: this.ezValue
     * }
     */
    constructor(name, value, optionalData) {
        if (!EzString.hasLength(name)) {
            throw new EzBadParamException(
                'name',
                EzEnumerationProperty,
                EzEnumerationProperty.constructor);
        }

        this.#ezName = name;

        this.#ezValue = EzObject.assignOrNull(value);

        this.#ezData = EzObject.assignOrDefault(
            optionalData,
            {
                enumName: this.ezName,
                enumValue: this.ezValue
            });
    }
}

/**
 * @class
 * @description
 * A base class thta provides 'enumeration class' functionality any
 * class that extends EzEnumeration2.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Adds the following static methods to the extended class:
 *      {EnumClass}.ezAsEnum(enumPropertyNameOrValue)
 *      {EnumClass}.ezAsEnumOrDefault(enumPropertyNameOrValue, defaultEnumPropertyValue)
 *      {EnumClass}.ezValueOf(enumPropertyName)
 *      {EnumClass}.ezValueOfOrDefault(enumPropertyName, defaultEnumPropertyValue)
 *      {EnumClass}.ezNameOf(enumPropertyValue)
 *      {EnumClass}.ezNameOfOrDefault(enumPropertyValue, defaultEnumPropertyValue)
 *      {EnumClass}.ezEnumData(enumPropertyValue)
 *      {EnumClass}.ezEnumDataOrDefault(enumPropertyValue, defaultEnumData)
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzEnumeration2 {
    /**
     * @public @static @readonly @property
     * Gets the EzApi 'ready' event name: 'onEzApiReady'
     * @returns {string}
     */
    static get ezOnEzApiReadyEventName() {
        return 'onEzApiReady';
    }

    /**
     * @public @static @method
     * Adds a document event listener for the provided readyEventName that calls the provided
     * ezRegistrator function.
     * @param {string} readyEventName
     * Default: null
     * @param {function} readyCallbackFunction
     * Default: () => { globalThis.ezApi.ezclocker.ezLogger.error('))})
     */
    static ezWaitReady(readyEventName = 'onReady', readyCallback = null) {
        if (!EzString.hasText(readyEventName)) {
            throw new EzBadParamException(
                'readyEventName',
                EzEnumeration2,
                EzEnumeration2.ezWaitReady);
        }
        if (!EzFunction.isFunction(readyCallback)) {
            throw new EzBadParamException(
                'ezRegistrator',
                EzEnumeration2,
                EzEnumeration2.ezWaitReady);
        }

        globalThis.document.addEventListener(
            readyEventName,
            readyCallback);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of the default enumeration property: 'UNKNOWN'
     * @returns {string}
     */
    static get DEFAULT_UNKNOWN_ENUM_PROP_NAME() {
        return 'UNKNOWN';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of the default enumeration property: 'UNKNOWN'
     * @returns {string}
     */
    static get DEFAULT_UNKNOWN_ENUM_PROP_VALUE() {
        return EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of the default enumeration property: 'UNKNOWN'
     * @returns {string}
     */
    static get DEFAULT_UNKNOWN_ENUM_DATA() {
        return {
            display: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME,
            value: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE,
        };
    }

    /**
     * Gets the name of the property that will return the array ofenumeration property names.
     * @static
     * @public
     * @readonly
     * @property
     * @returns {string}
     */
    static get NAMES_PROPERTY_NAME() {
        return 'ezNames';
    }

    /**
     * Gets the name of the property that will return the array of enumeration property values.
     * @static
     * @public
     * @readonly
     * @property
     * @returns {string}
     */
    static get VALUES_PROPERTY_NAME() {
        return 'ezValues';
    }

    /**
     * Gets the name of the property that will return the array of enumeration property data.
     * @static
     * @public @readonly @property
     * @returns {string}
     */
    static get DATA_PROPERTY_NAME() {
        return 'ezData';
    }

    /**
     * Gets the name of the method 'ezNameOf' (to avoid typos)
     * @static
     * @public @readonly @property
     * @returns {string}
     */
    static get NAME_OF_METHOD_NAME() {
        return 'ezNameOf';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of the method 'ezNameOfOrDefaultf' (to avoid typos)
     * @returns {string}
     */
    static get NAME_OF_OR_DEFAULT_METHOD_NAME() {
        return 'ezNameOfOrDefaultf';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of the static method 'ezValueOf' (to avoid typos)
     * @returns {string}
     */
    static get VALUE_OF_METHOD_NAME() {
        return 'ezValueOf';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of the static method 'ezValueOfOrDefault' (to avoid typos)
     * @returns {string}
     */
    static get VALUE_OF_OR_DEFAULT_METHOD_NAME() {
        return 'ezValueOfOrDefault';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of the static method 'ezAsEnum' (to avoid typos)
     * @returns {string}
     */
    static get AS_ENUM_METHOD_NAME() {
        return 'ezAsEnum';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of the static method 'ezAsEnumOrDefault' (to avoid typos)
     * @returns {string}
     */
    static get AS_ENUM_OR_DEFAULT_METHOD_NAME() {
        return 'ezAsEnumOrDefault';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of the static method 'ezEnumData' (to avoid typos)
     * @returns {string}
     */
    static get ENUM_DATA_METHOD_NAME() {
        return 'ezEnumData';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of the static method 'ezEnumDataOrDefault' (to avoid typos)
     * @returns {string}
     */
    static get ENUM_DATA_OR_DEFAULT_METHOD_NAME() {
        return 'ezEnumDataOrDefault';
    }

    /**
     * @private
     * Stores the array of EzEnumerationProperty instances for the 'enumeration' class that extends EzEnumeration2.
     * @type {array}
     */
    #ezEnumProps = [];

    /**
     * @public @method
     * Builds an enumeration property
     * @param {string} name
     * @param {number|string|boolean|*} value
     * Default: null
     * If undefined or null then the name is used as the value.
     * @param {undefined|null|object|*} optionalData
     * Default: null
     * If undefined or null then the following default data object is used:
     *  {
     *      name,
     *      value: EzString.stringWithLengthOrDefault(
     *          value,
     *          name)
     *  }
     */
    ezBuildEnumProp(name, value = null, optionalData = null) {
        if (!EzString.hasLength(name)) {
            throw new EzBadParamException(
                'name',
                this,
                this.ezBuildEnumProp);
        }

        const enumProperty = new EzEnumerationProperty(
            name,
            EzString.stringWithLengthOrDefault(
                value,
                name),
            EzObject.isValid(optionalData)
                ? optionalData
                : {
                    name,
                    value: EzString.stringWithLengthOrDefault(
                        value,
                        name)
                });

        this.#ezEnumProps.push(enumProperty);

        this.constructor[EzEnumeration2.NAMES_PROPERTY_NAME].push(enumProperty.ezName);

        this.constructor[EzEnumeration2.VALUES_PROPERTY_NAME].push(enumProperty.ezValue);

        this.constructor[EzEnumeration2.DATA_PROPERTY_NAME].push(enumProperty.ezData);

        this.constructor[enumProperty.ezName] = enumProperty.ezValue;

        this.#ezCreateStaticEnumerationMethods();
    }

    /**
     * @private @method
     * Creates the EzEnumerationProperties with the values
     * from the provided arrays.
     * @param {array} names
     * Default: []
     * Array must have a length of 1 or greater
     * @param {undefined|null|array} values
     * Default: null
     * If values is a valid array then it's length must equal the length of the names array.
     * @param {undefined|null|array} optionalData
     * Default: null
     * If optionalData is a valid array then it's length must equal the length of the names array.
     */
    #ezCreateEnumerationProperties(names = EzArray.EMPTY, values = null, optionalData = null) {
        if (!EzArray.hasLength(names)) {
            throw new EzBadParamException(
                'names',
                this,
                this.#ezCreateEnumerationProperties);
        }
        if (EzArray.hasLength(values)) {
            if (names.length !== values.length) {
                throw new EzBadParamException(
                    'values',
                    this,
                    this.#ezCreateEnumerationProperties);
            }
        } else {
            values = [
                ...names
            ];
        }
        if (EzArray.hasLength(optionalData)) {
            if (names.length !== optionalData.length) {
                throw new EzBadParamException(
                    'optionalData',
                    this,
                    this.#ezCreateEnumerationProperties);
            }
        } else {
            optionalData = [];
        }

        this.constructor[EzEnumeration2.NAMES_PROPERTY_NAME] = [];

        this.constructor[EzEnumeration2.VALUES_PROPERTY_NAME] = [];

        this.constructor[EzEnumeration2.DATA_PROPERTY_NAME] = [];

        for (let index = 0; index < names.length; index++) {
            const enumName = names[index];

            if (EzString.hasLength(enumName)) {
                const enumValue = index < values.length
                    ? values[index]
                    : enumName;

                const enumData = index < optionalData.length
                    ? optionalData[index]
                    : {
                        name: enumName,
                        value: enumValue
                    };

                const enumProperty = new EzEnumerationProperty(
                    enumName,
                    enumValue,
                    enumData);

                this.#ezEnumProps.push(enumProperty);

                this.constructor[EzEnumeration2.NAMES_PROPERTY_NAME].push(enumProperty.ezName);

                this.constructor[EzEnumeration2.VALUES_PROPERTY_NAME].push(enumProperty.ezValue);

                this.constructor[EzEnumeration2.DATA_PROPERTY_NAME].push(enumProperty.ezData);

                this.constructor[enumProperty.ezName] = enumProperty.ezValue;
            }
        }
    }

    /**
     * @private @method
     * Creates the static enumeration methods on the constructor of the class that
     * extended EzEnumeration2.
     */
    #ezCreateStaticEnumerationMethods() {
        const clazz = this.constructor;

        this.constructor[EzEnumeration2.NAME_OF_METHOD_NAME] = (enumPropertyValue) => this.#ezNameOf(
            clazz,
            enumPropertyValue);

        this.constructor[EzEnumeration2.NAME_OF_OR_DEFAULT_METHOD_NAME] = (enumPropertyValue, defaultEnumPropertyValue) => this.#ezNameOfOrDefault(
            clazz,
            enumPropertyValue,
            defaultEnumPropertyValue);

        this.constructor[EzEnumeration2.VALUE_OF_METHOD_NAME] = (enumPropertyName) => this.#ezValueOf(
            clazz,
            enumPropertyName);

        this.constructor[EzEnumeration2.VALUE_OF_OR_DEFAULT_METHOD_NAME] = (enumPropertyName, defaultEnumPropertyValue) => this.#ezValueOfOrDefault(
            clazz,
            enumPropertyName,
            defaultEnumPropertyValue);

        this.constructor[EzEnumeration2.AS_ENUM_METHOD_NAME] = (enumPropertyNameOrValue) => this.#ezAsEnum(
            clazz,
            enumPropertyNameOrValue);

        this.constructor[EzEnumeration2.AS_ENUM_OR_DEFAULT_METHOD_NAME] = (enumPropertyNameOrValue, defaultEnumPropertyNameOrValue) => this.#ezAsEnumOrDefault(
            clazz,
            enumPropertyNameOrValue,
            defaultEnumPropertyNameOrValue);

        this.constructor[EzEnumeration2.ENUM_DATA_METHOD_NAME] = (enumPropertyNameOrValue) => this.#ezEnumData(
            clazz,
            enumPropertyNameOrValue);

        this.constructor[EzEnumeration2.ENUM_DATA_OR_DEFAULT_METHOD_NAME] = (enumPropertyNameOrValue, defaultEnumData) => this.#ezEnumDataOrDefault(
            clazz,
            enumPropertyNameOrValue,
            defaultEnumData);
    }

    /**
     * @private @method
     * Returns the enum property name associated with the provided enumPropertyValue.
     * If the provided enumPropertyValue does not match an existing enumeration
     * property value then the then enum property name at index 0 in the
     * ezNames[] array is returned.
     * @param {class} clazz
     * A valid ezClocker enumeration class extended from EzEnumeration2
     * @param {string} enumPropertyName
     * Default: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME
     * A valid enumeration property value from this enumeration class.
     * @returns {string}
     * A valid enumeration property name from this enumeration class.
     */
    #ezNameOf(clazz, enumPropertyName = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME) {
        if (!EzObject.isValid(clazz)) {
            throw new EzBadParamException(
                'clazz',
                this,
                this.#ezNameOf);
        }

        if (!EzObject.isValid(enumPropertyName)) {
            enumPropertyName = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME;
        }

        if (!clazz?.[EzEnumeration2.VALUES_PROPERTY_NAME]) {
            throw new EzBadStateException(
                `Expected class ${clazz.name} to have static property ${clazz.name}.${EzEnumeration2.VALUES_PROPERTY_NAME}.`,
                `Class ${clazz.name} does not seem to have the static property ${clazz.name}.${EzEnumeration2.VALUES_PROPERTY_NAME}`,
                clazz,
                clazz['ezAsEnum']);
        }
        if (!clazz?.[EzEnumeration2.NAMES_PROPERTY_NAME]) {
            throw new EzBadStateException(
                `Expected class ${clazz.name} to have static property ${clazz.name}.${EzEnumeration2.NAMES_PROPERTY_NAME}.`,
                `Class ${clazz.name} does not have the static property ${clazz.name}.${EzEnumeration2.NAMES_PROPERTY_NAME}`,
                clazz,
                clazz['ezAsEnum']);
        }

        if (EzArray.hasLength(clazz?.[EzEnumeration2.VALUES_PROPERTY_NAME])) {
            const valueIndex = clazz[EzEnumeration2.VALUES_PROPERTY_NAME].indexOf(enumPropertyName);

            if (0 <= valueIndex) {
                return clazz[EzEnumeration2.NAMES_PROPERTY_NAME][valueIndex];
            }
        }

        return null;
    }

    /**
     * @private @method
     * Returns the enum property name associated with the provided enumPropertyValue.
     * If the provided enumPropertyValue does not match an existing enumeration
     * property value then the then enum property name at index 0 in the
     * ezNames[] array is returned.
     * If the provided enumPropertyName is not a valid enumeration property name, then the provided defaultEnumPropertyValue is returned.
     * @param {class} clazz
     * A valid ezClocker enumeration class extended from EzEnumeration2
     * @param {string} enumPropertyName
     * Default: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME
     * A valid enumeration property value from this enumeration class.
     * @param {undefined|null|string|any} defaultEnumPropertyValue
     * Default: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE
     * @returns {string|any}
     * A valid enumeration property name from this enumeration class.
     */
    #ezNameOfOrDefault(clazz, enumPropertyName = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME,
        defaultEnumPropertyValue = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE) {
        if (!EzObject.isValid(clazz)) {
            throw new EzBadParamException(
                'clazz',
                this,
                this.#ezAsEnumOrDefault);
        }

        if (!EzString.hasText(enumPropertyName)) {
            return defaultEnumPropertyValue;
        }

        const matchingEnumPropertyValue = clazz.ezNameOf(enumPropertyName);

        return EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE === matchingEnumPropertyValue
            ? defaultEnumPropertyValue
            : matchingEnumPropertyValue;
    }

    /**
     * @public @static @method
     * Returns the enum property value for the provided enumPropertyName
     * If the provided enumPropertyName does not match an existing enumeration
     * property then the value of the enum property name at index 0 in the ezNames[]
     * array is returned.
     * @param {class} clazz
     * A valid ezClocker enumeration class extended from EzEnumeration2
     * @param {undefined|null|string} enumPropertyName
     * Default: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME
     * A valid enumeration property name from this enumeration class.
     * @returns string}
     * A valid enumeration property value from this enumeration class.
     */
    #ezValueOf(clazz, enumPropertyName = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME) {
        if (!EzObject.isValid(clazz)) {
            throw new EzBadParamException(
                'clazz',
                this,
                this.#ezValueOf);
        }

        if (!EzObject.isValid(enumPropertyName)) {
            enumPropertyName = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME;
        }

        if (!clazz?.[EzEnumeration2.NAMES_PROPERTY_NAME]) {
            throw new EzBadStateException(
                `Expected class ${clazz.name} to have static property ${clazz.name}.${EzEnumeration2.NAMES_PROPERTY_NAME}.`,
                `Class ${clazz.name} does not have the static property ${clazz.name}.${EzEnumeration2.NAMES_PROPERTY_NAME}`,
                clazz,
                clazz['ezValueOf']);
        }

        if (EzArray.hasLength(clazz?.[EzEnumeration2.NAMES_PROPERTY_NAME])) {
            const nameIndex = clazz[EzEnumeration2.NAMES_PROPERTY_NAME].indexOf(enumPropertyName);

            if (0 <= nameIndex) {
                return clazz[clazz[EzEnumeration2.NAMES_PROPERTY_NAME][nameIndex]];
            }
        }

        return null;
    }

    /**
     * @static
     * @public @function
     * Gets the EzDataTagType enum property value for the provided ezDataTagTypeEnumPropertyValue. If the provided ezDataTagTypeEnumPropertyValue
     * is not a valid EzDataTagType enum property value (or results in EzDataTagType.UNKNOWN), then the provided defaultEzDataTagType value is returned.
     * @param {undefined|null|string} enumPropertyValue
     * Default: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE
     * @param {undefined|null|string|any} defaultEnumPropertyValue
     * Default: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE
     * @returns {string|any}
     * A valid EzDataTagType enum property value matching the provided ezDataTagTypeEnumPropertyValue or the provided defaultEzDataTagType
     */
    #ezValueOfOrDefault(clazz, enumPropertyName = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME,
        defaultEnumPropertyValue = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE) {
        if (!EzObject.isValid(clazz)) {
            throw new EzBadParamException(
                'clazz',
                this,
                this.#ezAsEnumOrDefault);
        }

        if (!EzString.hasText(enumPropertyName)) {
            return defaultEnumPropertyValue;
        }

        const matchingEnumPropertyValue = clazz.ezValueOf(enumPropertyName);

        return EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE === matchingEnumPropertyValue
            ? defaultEnumPropertyValue
            : matchingEnumPropertyValue;
    }

    /**
     * @public @static @method
     * Returns the enum property value for the provided enumPropertyNameOrValue which can be either
     * a enumeration property name OR a enumeration property value.
     * If the provided enumPropertyNameOrValue does not match an existing enumeration property value
     * then an attempt is made to match a enum property name.
     * If the enumPropertyNameOrValue does not match an enum property name OR enum property value then
     * the value of the enum property name at index 0 in the ezNames[] array is returned.
     * @param {class} clazz
     * A valid ezClocker enumeration class extended from EzEnumeration2
     * @param {undefined|null|string} enumPropertyNameOrValue
     * Default: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE
     * A valid enumeration property name OR enum property value from this enumeration class.
     * @returns {String}
     * A valid enumeration property value from this enumeration class.
     */
    #ezAsEnum(clazz, enumPropertyNameOrValue = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE) {
        if (!EzObject.isValid(clazz)) {
            throw new EzBadParamException(
                'clazz',
                this,
                this.#ezAsEnum);
        }

        if (!EzObject.isValid(enumPropertyNameOrValue)) {
            enumPropertyNameOrValue = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE;
        }

        if (!clazz?.[EzEnumeration2.NAMES_PROPERTY_NAME]) {
            throw new EzBadStateException(
                `Expected class ${clazz.name} to have static property ${clazz.name}.${enumPropertyNameOrValue}.`,
                clazz,
                clazz.ezAsEnum);
        }
        if (!clazz?.[EzEnumeration2.VALUES_PROPERTY_NAME]) {
            throw new EzBadStateException(
                `Expected class ${clazz.name} to have a static property with value ${enumPropertyNameOrValue}.`,
                clazz,
                clazz.ezAsEnum);
        }

        if (EzArray.hasLength(clazz?.[EzEnumeration2.NAMES_PROPERTY_NAME])) {
            const nameIndex = clazz[EzEnumeration2.NAMES_PROPERTY_NAME].indexOf(enumPropertyNameOrValue);

            if (0 <= nameIndex) {
                const enumPropertyName = clazz[EzEnumeration2.NAMES_PROPERTY_NAME][nameIndex];

                if (!clazz?.[enumPropertyName]) {
                    throw new EzBadStateException(
                        `Expected class ${clazz.name} to have static property ${clazz.name}.${enumPropertyName}`,
                        `However, the class ${clazz.name} does not seem to have a static property named ${clazz.name}.${enumPropertyName}`,
                        clazz,
                        clazz['ezAsEnum']);
                }

                return clazz[enumPropertyName];
            }
        }

        if (EzArray.hasLength(clazz?.[EzEnumeration2.VALUES_PROPERTY_NAME])) {
            const valueIndex = clazz[EzEnumeration2.VALUES_PROPERTY_NAME].indexOf(enumPropertyNameOrValue);

            if (0 <= valueIndex && EzArray.hasLength(clazz?.[EzEnumeration2.NAMES_PROPERTY_NAME])) {
                const enumPropertyName = clazz[EzEnumeration2.NAMES_PROPERTY_NAME][valueIndex];

                if (!clazz?.[enumPropertyName]) {
                    throw new EzBadStateException(
                        `Expected class ${clazz.name} to have static property ${clazz.name}.${enumPropertyName}`,
                        `However, the class ${clazz.name} does not seem to have a static property named ${clazz.name}.${enumPropertyName}`,
                        clazz,
                        clazz['ezAsEnum']);
                }

                return clazz[enumPropertyName];
            }
        }

        // Returning the first property
        throw new EzBadStateException(
            EzString.em`
                Expected class ${clazz.name} to have static property ${clazz.name}.${enumPropertyNameOrValue}
                or a static property with the value of ${enumPropertyNameOrValue}`,
            EzString.em`
                However, the class ${clazz.name} does not seem to have a static property named ${clazz.name}.${enumPropertyNameOrValue}
                or a static property with the value of ${enumPropertyNameOrValue}`,
            clazz,
            clazz['ezAsEnum']);
    }

    /**
     * @static
     * @public @function
     * Gets the EzDataTagType enum property value for the provided ezDataTagTypeEnumPropertyValue. If the provided ezDataTagTypeEnumPropertyValue
     * is not a valid EzDataTagType enum property value (or results in EzDataTagType.UNKNOWN), then the provided defaultEzDataTagType value is returned.
     * @param {undefined|null|string} enumPropertyValue
     * Default: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE
     * @param {undefined|null|string|any} defaultEnumPropertyValue
     * Default: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE
     * @returns {string|any}
     * A valid EzDataTagType enum property value matching the provided ezDataTagTypeEnumPropertyValue or the provided defaultEzDataTagType
     */
    #ezAsEnumOrDefault(clazz, enumPropertyValue = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE,
        defaultEnumPropertyValue = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE) {
        if (!EzObject.isValid(clazz)) {
            throw new EzBadParamException(
                'clazz',
                this,
                this.#ezAsEnumOrDefault);
        }

        if (!EzString.hasText(enumPropertyValue)) {
            return defaultEnumPropertyValue;
        }

        const matchingEnumPropertyValue = clazz.ezAsEnum(enumPropertyValue);

        return EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE === matchingEnumPropertyValue
            ? defaultEnumPropertyValue
            : matchingEnumPropertyValue;
    }

    /**
     * @static
     * @private @method
     * Obtains the enumeration data (if available) for the provided enumPropertyNameOrValue.
     * If the enumPropertynameOrValue provided doesn't match any enum propert names or values then the
     * default data value is returned (normally, this is the value fro the UNKNOWN property name.)
     * @param {class} clazz
     * A valid ezClocker enumeration class that extends EzEnumeration2
     * @param {string} enumPropertyNameOrValue
     * Default: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE
     * @returns {null|*}
     * Returns the enum property's data (if it exists)
     * If the enum property does not have data, then null is returned.
     */
    #ezEnumData(clazz, enumPropertyNameOrValue = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE) {
        if (!EzObject.isValid(clazz)) {
            throw new EzBadParamException(
                'clazz',
                this,
                this.#ezEnumData);
        }

        if (!EzObject.isValid(enumPropertyNameOrValue)) {
            enumPropertyNameOrValue = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE;
        }

        if (!clazz?.[EzEnumeration2.NAMES_PROPERTY_NAME]) {
            throw new EzBadStateException(
                `Expected class ${clazz.name} to have static property ${clazz.name}.${EzEnumeration2.NAMES_PROPERTY_NAME}.`,
                `Class ${clazz.name} does not have the static property ${clazz.name}.${EzEnumeration2.NAMES_PROPERTY_NAME}`,
                clazz,
                clazz['ezAsEnum']);
        }
        if (!clazz?.[EzEnumeration2.VALUES_PROPERTY_NAME]) {
            throw new EzBadStateException(
                `Expected class ${clazz.name} to have static property ${clazz.name}.${EzEnumeration2.VALUES_PROPERTY_NAME}.`,
                `Class ${clazz.name} does not seem to have the static property ${clazz.name}.${EzEnumeration2.VALUES_PROPERTY_NAME}`,
                clazz,
                clazz['ezAsEnum']);
        }

        if (EzArray.hasLength(clazz?.[EzEnumeration2.VALUES_PROPERTY_NAME])) {
            const valueIndex = clazz[EzEnumeration2.VALUES_PROPERTY_NAME].indexOf(enumPropertyNameOrValue);

            if (0 <= valueIndex && valueIndex < clazz[EzEnumeration2.DATA_PROPERTY_NAME].length) {
                if (!EzArray.hasLength(clazz?.[EzEnumeration2.DATA_PROPERTY_NAME])) {
                    // No data for this enumeration, returning a default enumeration data instance
                    return new EzBasicEnumerationData(
                        clazz,
                        clazz[EzEnumeration2.NAMES_PROPERTY_NAME][valueIndex]);
                }

                return clazz[EzEnumeration2.DATA_PROPERTY_NAME][valueIndex];
            }
        }

        if (EzArray.hasLength(clazz?.[EzEnumeration2.NAMES_PROPERTY_NAME])) {
            const nameIndex = clazz[EzEnumeration2.NAMES_PROPERTY_NAME].indexOf(enumPropertyNameOrValue);

            if (!EzArray.hasLength(clazz?.[EzEnumeration2.DATA_PROPERTY_NAME])) {
                // No data for this enumeration, returning a default enumeration data instance
                return new EzBasicEnumerationData(
                    clazz,
                    clazz[EzEnumeration2.VALUES_PROPERTY_NAME][nameIndex]);
            }

            if (0 <= nameIndex && nameIndex < clazz[EzEnumeration2.DATA_PROPERTY_NAME].length) {
                return clazz[EzEnumeration2.DATA_PROPERTY_NAME][nameIndex];
            }
        }

        const errorEnumData = {
            errorCode: 404,
            enumName: enumPropertyNameOrValue,
            message: `Enumeration with enumeration property name of value equal to '${enumPropertyNameOrValue}' does not exist.`
        };

        // Enumeration uknown, returning an error data instance
        globalThis.ezApi.ezclocker.ezLogger.error(errorEnumData.message);

        return errorEnumData;
    }

    /**
     * @static
     * @public @function
     * Gets the EzDataTagType enum property value for the provided ezDataTagTypeEnumPropertyValue. If the provided ezDataTagTypeEnumPropertyValue
     * is not a valid EzDataTagType enum property value (or results in EzDataTagType.UNKNOWN), then the provided defaultEzDataTagType value is returned.
     * If the provided enumPropertyName is not a valid enumeration property name, then the provided defaultEnumData is returend instead.
     * @param {undefined|null|string} enumPropertyNameOrValue
     * Default: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME
     * @param {undefined|null|string|any} defaultEnumData
     * Default:
     *   EzEnumeration2.DEFAULT_UNKNOWN_ENUM_DATA = {
     *       display: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME,
     *       value: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_VALUE,
     *   };
     * @returns {string|any}
     * A valid EzDataTagType enum property value matching the provided ezDataTagTypeEnumPropertyValue or the provided defaultEzDataTagType
     */
    #ezEnumDataOrDefault(clazz, enumPropertyNameOrValue = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME,
        defaultEnumData = EzEnumeration2.DEFAULT_UNKNOWN_ENUM_DATA) {
        if (!EzObject.isValid(clazz)) {
            throw new EzBadParamException(
                'clazz',
                this,
                this.#ezEnumDataOrDefault);
        }

        if (!EzString.hasText(enumPropertyNameOrValue)) {
            return defaultEnumData;
        }

        const enumPropertyNameData = clazz.ezEnumData(enumPropertyNameOrValue);

        return !enumPropertyNameData
            ? defaultEnumData
            : enumPropertyNameData;
    }

    /**
     * @private @method
     * Exposes the enumeration on globalThis for debugging purposes.
     */
    #ezExposeEnumerationClassToGlobalThis() {
        const enumClassName = this?.constructor?.name || this?.default?.name || EzString.EMPTY;

        if (!EzString.hasLength(enumClassName)) {
            globalThis.console.error(
                EzString.em`
                    Failed to publish the enumeration class to the globalThis object due to the following error:
                    The guessed enumeration class name resulted in undefined, null, or an empty string.
                    [Guessed Enumeration Class Name: ${enumClassName}]
                    [Enumeration instance: ${EzJson.toJson(this)}]
                    [Enumeration class: ${EzJson.toJson(this?.constructor)}]
                    [Enumberation instance.default.name: ${this?.default?.name}]
                    [Enumeration class.constructor.name: ${this.constructor.name}]`);

            return;
        }

        if ('EzEnumeration2' === enumClassName) {
            globalThis.console.error(
                EzString.em`
                    Failed to publish the enumeration class to the globalThis object due to the following error:
                    The enumeration class is using the default class name of 'EzEnumeration2'.
                    [Guessed Enumeration Class Name: ${enumClassName}]
                    [Enumeration instance: ${EzJson.toJson(this)}]
                    [Enumeration class: ${EzJson.toJson(this?.constructor)}]
                    [Enumberation instance.default.name: ${this?.default?.name}]
                    [Enumeration class.constructor.name: ${this.constructor.name}]`);

            return;
        }

        globalThis[enumClassName] = this?.constructor;
    }

    /**
     * @public @constructor
     * >> DO NOT CREATE NEW INSTANCES <<
     * The static initializer will create a singleton instance that injects the static properties and methods
     * that you then use to access enumeration values.
     * Creates a new instance of EzEnumeration2.
     * Upon construction, EzEnumeration2 will inject static fields for each enumeration property name.
     * Example enumeration property access:
     *     MyEnunmType.PROPERTY_NAME_1 (returns the value for PROPERTY_NAME_1)
     *     MyEnunmType.PROPERTY_NAME_2 (returns the value for PROPERTY_NAME_1)
     *     ...
     *     MyEnunmType.PROPERTY_NAME_n (returns the value for PROPERTY_NAME_n)
     * In addition to the static properties the following three static methods get added to the implementing class
     * that provide some 'common' functionality for all enumerations:
     *     1) MyEnumType.ezValueOf(propertyName) (returns the property value that matches the propertyName)
     *     2) MyEnumType.ezNameOf(propertyValue) (returns the name that matches the propertyValue)
     *     3) MyEnumType.ezAsEnum(propertyName_or_propertyValue) (returns the matching enum property name)
     * @param {undefined|null|array} names
     * * If the names array is undefined, null, or an empty array then the default names array is used.
     *   Default names array is:
     *   [
     *     'UNKNOWN'
     *   ]
     * @param {undefined|null|array} values
     * * If the values array is undefiend or null then the values array is assumed to equal the
     *   provided names array.
     * * If the values array is not undefined and not null then
     *   the length of the values array is required to equal the length of the provided names array.
     * @param {undefined|null|array} optionalData
     * * If the optionalData array is provided and has a length then
     *   the length of the optionalData array is required to equal the length of the provided names array.
     * * If the optionalData array is undefined or null then a default data object is created for each
     *   property in the names array. The default data object is:
     *   {
     *     name: enumName,
     *     value: enumValue
     *   }
     */
    constructor(names, values, optionalData) {
        const prependedUnknownProp = !EzArray.hasLength(names) || -1 === names.indexOf(EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME);

        if (EzBoolean.isTrue(prependedUnknownProp)) {
            names = EzArray.arrayHasLength(names)
                ? [
                    ...[EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME],
                    ...names
                ]
                : [EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME];
        }

        if (EzArray.hasLength(values)) {
            if (names.length > values.length) {
                throw new EzBadParamException(
                    'values',
                    EzEnumeration2,
                    EzEnumeration2.constructor);
            }
            if (EzBoolean.isTrue(prependedUnknownProp)) {
                values = [
                    ...[EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME],
                    ...values
                ];
            }
        } else {
            values = [...names];
        }

        if (EzArray.isArray(optionalData)) {
            if (names.length > optionalData.length) {
                throw new EzBadParamException(
                    'optionalData',
                    EzEnumeration2,
                    EzEnumeration2.constructor);
            }

            if (EzBoolean.isTrue(prependedUnknownProp)) {
                optionalData = [
                    {
                        name: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME,
                        value: EzEnumeration2.DEFAULT_UNKNOWN_ENUM_PROP_NAME
                    },
                    ...optionalData
                ];
            }
        } else {
            optionalData = null;
        }

        this.#ezCreateEnumerationProperties(names, values, optionalData);

        this.#ezCreateStaticEnumerationMethods();

        this.#ezExposeEnumerationClassToGlobalThis();
    }
}

/*
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Enumeration Template
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */

// import { EzString } from '/ezlibrary/helpers/EzString.js';
// import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';
//
// /**
//  * @class
//  * @extends {EzEnumeration2}
//  * @description
//  * A template for an ezClocker enumeration class.
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * The following public static methods provided by the EzEnumeration2 parent class:
//  *
//  *      /* Get the enum property value of the enum property name or the enum property value that matches the provided enumPropertyNameOrValue */
//  *      EzExportAction.ezAsEnum(enumPropertyNameOrValue)
//  *      Examples:
//  *           const enumPropertyValue = EzEnumTemplate.ezAsEnum('ENUM_1');
//  *           const enumPropertyValue = EzEnumTemplate.ezAsEnum('E1');
//  *           const enumPropertyValue = EzEnumTemplate.ezAsEnum(EzEnumTemplate.ENUM_1);
//  *
//  *      /* Get the enum property value of the enum property name that equals the provided enumPropertyName */
//  *      EzExportAction.ezValueOf(enumPropertyName)
//  *      Examples:
//  *           const enumPropertyValue = EzEnumTemplate.ezValueOf('ENUM_1');
//  *
//  *      /* Get the enum property who's value equals the provided enumPropertyValue */
//  *      EzExportAction.ezNameOf(enumPropertyValue)
//  *      Examples:
//  *           const enumPropertyName = EzEnumTemplate.ezNameOf('E1');
//  *
//  *      /* Get the enum property's data object for the enum property name that matches the provided enumPropertyValue. */
//  *      EzExportAction.ezEnumData(enumPropertyValue)
//  *      Examples:
//  *           const enumData = EzEnumTemplate.ezEnumData(EzEnumTemplate.ENUM_1);
//  *           const enumData = EzEnumTemplate.ezEnumData('E1');
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * Import into non-enumeration classes with:
//  *      import {
//  *          // ... other enumeration classes ...
//  *          EzEnumTemplate
//  *      } from '/ezlibrary/enums/EzEnumerations.js';
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * Import into other enumeration classes or classes that cannot import EzEnumerations:
//  *      import { EzEnumTemplate } from '/ezlibrary/enums/EzEnumTemplate.js';
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * Static access to methods, fields, and properties only!
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  */
// export class EzEnumTemplate extends EzEnumeration2 {
//      /**
//       * @static
//       * @public @method
//       * Example static method that returns the enumeration property's data.display value.
//       * If the enumPropertyValue is not a valid enumeration property value, then EzEnumTemplate.UNKNOWN.display is returned.
//       * @param {undefined|null|string} enumPropertyValue
//       * A valid enumeration property value from EzEnumTemplate
//       * @returns {string}
//       */
//      static ezToDisplayName(enumPropertyValue) {
//          return EzString.stringOrEmpty(
//              EzString.hasLength(enumPropertyValue)
//                  ? EzEnumTemplate.ezEnumData(enumPropertyValue)?.display
//                  : EzEnumTemplate.ezEnumData(EzEnumTemplate.UNKNOWN)?.display);
//      }
//
//     /*
//      * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//      * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//      * Initialization Section: ONLY PLACE INITIALIZATION CODE BEYOND THIS SECTION
//      * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//      * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//      */
//
//     /**
//      * @private
//      * Stores the singleton instance of this enumeration class.
//      * @type {EzEnumTemplate}
//      * Internal static instance of the EzEnumTemplate
//      */
//     static #ezEnumTemplateSingleton = null;
//
//     /**
//      * @static
//      * @private @method
//      * Builds the enumeration properties and values for EzClockerAccountType
//      */
//     static #ezBuildEnumeration() {
//          this.#ezEnumTemplateSingleton.ezBuildEnumProp(
//              // Enumeration Property Name
//              'UNKNOWN',
//              // Enumeration Property Value
//              'UNKNOWN',
//              // Optional Enumeration Data Value
//              {
//                  value: 'UNKNOWN',
//                  display: 'UNKNOWN'
//              });
//
//          this.#ezEnumTemplateSingleton.ezBuildEnumProp(
//              'ENUM_1',
//              'E1',
//              {
//                  value: 'E2',
//                  display: 'Enum 1'
//              });
//
//          this.#ezEnumTemplateSingleton.ezBuildEnumProp(
//              'ENUM_2',
//              'E2',
//              {
//                  value: 'E2',
//                  display: 'Enum 2'
//              });
//
//          this.#ezEnumTemplateSingleton.ezBuildEnumProp(
//              'ENUM_3',
//              'E3',
//              {
//                  value: 'E3',
//                  display: 'Enum 3'
//              });
//      }
//
//      /**
//       * Static Initialization of EzExportAction
//       */
//      static {
//          if (null == this.#ezEnumTemplateSingleton) {
//              this.#ezEnumTemplateSingleton = new EzEnumTemplate();
//
//              this.#ezBuildEnumeration();
//
//              globalThis.EzEnumTemplate = this;
//          }
//      }
//
//    /*
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * End of Initialziation Section: !! DO NOT PLACE ANY CODE BEYOND THIS SECTION !!
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
// }
